import * as THREE from 'three';
import cardVertexShader from '../shaders/cardVertexShader.glsl';
import cardFragmentShader from '../shaders/cardFragmentShader.glsl';
import FloatingCards from '../FloatingCards.js';
import { ATLAS, CARD } from '../constants/cardsConstants.js';

const atlasTextureURL = `${window.theme_assets_url}/images/global-opportunity/atlas-poststamps.png`;
export default class CardSystem{
  constructor(physicsSimulator) {
    this.floatingCards = FloatingCards.getInstance();
    this.camera = this.floatingCards.camera;
    this.scene = this.floatingCards.scene;
    this.cards = this.floatingCards.cards;
    this.time = this.floatingCards.time;
    this.physicsSimulator = physicsSimulator;
    this.WIDTH = this.physicsSimulator.WIDTH;

    // Sort cards by order but keep original indices
    this.sortedCards = this.cards.cards.map((card, index) => ({
      card,
      originalIndex: index
    })).sort((a, b) => {
      const orderA = a.card[1].world.order || 0;
      const orderB = b.card[1].world.order || 0;
      return orderA - orderB;
    });

    this.PARTICLES = this.sortedCards.length;

    // Animation properties
    this.animationStartTime = null;
    this.baseDelay = 0.2; // Base delay between individual cards
    this.batchDelayMultiplier = 0.15; // Additional delay per batch
    this.appearDuration = 1.2; // Duration of appear animation
    this.hasCompletedAnimation = false;

    this.init();
  }

  async init() {
    await this.initCards();
    await this.loadTexture();
  }

  async loadTexture() {
    const textureLoader = new THREE.TextureLoader();
    return new Promise((resolve) => {
      textureLoader.load(
        atlasTextureURL,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.generateMipmaps = false;
          texture.anisotropy = this.floatingCards.renderer.instance.capabilities.getMaxAnisotropy();
          texture.needsUpdate = true;
          this.cardMesh.material.uniforms.atlasTexture.value = texture;
          resolve(texture);
        }
      );
    });
  }

  async initCards() {
    // Geometry and material for the cards
    const cardGeometry = new THREE.PlaneGeometry(1, 1);
    const cardMaterial = new THREE.ShaderMaterial({
      uniforms: {
        positionTexture: { value: null },
        atlasTexture: { value: null },
        resolution: { value: new THREE.Vector2(this.WIDTH, this.WIDTH) },
        currentTime: { value: 0.0 }
      },
      vertexShader: cardVertexShader,
      fragmentShader: cardFragmentShader,
      transparent: true,
      depthWrite: false,
      precision: 'highp'
    });

    // Create instanced mesh
    this.cardMesh = new THREE.InstancedMesh(cardGeometry, cardMaterial, this.PARTICLES);
    this.setupPerInstanceAttributes();
    this.scene.add(this.cardMesh);
  }

  startAnimation() {
    this.animationStartTime = this.time.elapsed;
    this.hasCompletedAnimation = false;
  }

  setupPerInstanceAttributes() {
    const particleIndices = new Float32Array(this.PARTICLES);
    const scales = new Float32Array(this.PARTICLES * 2);
    const uvOffsets = new Float32Array(this.PARTICLES * 2);
    const uvScales = new Float32Array(this.PARTICLES * 2);
    const rotations = new Float32Array(this.PARTICLES);
    const batchIndices = new Float32Array(this.PARTICLES);
    const delays = new Float32Array(this.PARTICLES);

    // Use sorted cards for attribute setup
    this.sortedCards.forEach(({ card, originalIndex }, index) => {
      const [key, cardData] = card;

      // Use original index for position texture lookup
      particleIndices[index] = originalIndex;

      scales[index * 2] = cardData.scaleX * 0.5;
      scales[index * 2 + 1] = cardData.scaleY * 0.5;

      const uvOffsetX = cardData.frame.x / ATLAS.WIDTH;
      const uvOffsetY = 1.0 - (cardData.frame.y + CARD.HEIGHT) / ATLAS.HEIGHT;
      const uvScaleX = CARD.WIDTH / ATLAS.WIDTH;
      const uvScaleY = CARD.HEIGHT / ATLAS.HEIGHT;

      uvOffsets[index * 2] = uvOffsetX;
      uvOffsets[index * 2 + 1] = uvOffsetY;
      uvScales[index * 2] = uvScaleX;
      uvScales[index * 2 + 1] = uvScaleY;

      rotations[index] = cardData.world.rotation ? (cardData.world.rotation * Math.PI / 180.0) : 0;
      batchIndices[index] = cardData.world.batch || 0;

      // Random delay within the batch
      delays[index] = Math.random() * this.baseDelay + this.baseDelay * 2;
    });

    this.cardMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.cardMesh.geometry.setAttribute('particleIndex', new THREE.InstancedBufferAttribute(particleIndices, 1));
    this.cardMesh.geometry.setAttribute('scale', new THREE.InstancedBufferAttribute(scales, 2));
    this.cardMesh.geometry.setAttribute('uvOffset', new THREE.InstancedBufferAttribute(uvOffsets, 2));
    this.cardMesh.geometry.setAttribute('uvScale', new THREE.InstancedBufferAttribute(uvScales, 2));
    this.cardMesh.geometry.setAttribute('rotation', new THREE.InstancedBufferAttribute(rotations, 1));
    this.cardMesh.geometry.setAttribute('batchIndex', new THREE.InstancedBufferAttribute(batchIndices, 1));
    this.cardMesh.geometry.setAttribute('delay', new THREE.InstancedBufferAttribute(delays, 1));
  }

  update(delta) {
    // Don't update animation until it's started
    if (this.animationStartTime === null) {
      return;
    }

    const currentTime = this.time.elapsed - this.animationStartTime;

    // Use more precise time value
    this.cardMesh.material.uniforms.currentTime.value = currentTime;

    // Check if animation is complete
    if (!this.hasCompletedAnimation) {
      const maxDelay = Math.max(...Array.from(this.cardMesh.geometry.attributes.delay.array)) +
                      Math.max(...Array.from(this.cardMesh.geometry.attributes.batchIndex.array)) * this.batchDelayMultiplier;

      if (currentTime > maxDelay + this.appearDuration) {
        this.hasCompletedAnimation = true;
        this.floatingCards.onAppearAnimationComplete();
      }
    }

    // Update position texture
    this.cardMesh.material.uniforms.positionTexture.value = this.physicsSimulator.getCurrentPositionTexture();
  }
}
